<template>
  <div class="nav-wrapper">
    <!-- Top strip shown above the main navigation (contains small links) -->
    <div class="hidden lg:block bg-gray-50 border-b border-gray-200">
      <div class="max-w-10xl mx-auto px-4 lg:px-8">
        <div class="flex justify-end items-center h-8">
          <div class="flex items-center gap-4">
            <!-- Dynamic top navigation links -->
            <component
              v-for="item in topNavItems"
              :key="item.key"
              :is="getLinkComponent(item.key)"
              v-bind="getLinkProps(item.key)"
              class="text-sm text-gray-600 hover:text-hongtu-primary transition-colors"
            >
              {{ item.text }}
            </component>

            <!-- language select -->
            <div class="flex items-center gap-2 pl-2 border-l border-gray-200">
              <label for="locale-select" class="sr-only">语言</label>
              <select id="locale-select" v-model="locale" @change="onLocaleChange"
                class="text-sm bg-transparent border-none focus:outline-none">
                <option value="zh">简体中文</option>
                <option value="zh-Hant">繁體中文</option>
                <option value="en">EN</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <header class="bg-white border-b border-gray-200">
      <div class=" mx-auto px-4 lg:px-8">
        <div class="flex items-center justify-between h-12 lg:h-16">
          <!-- Left side: Logo and Navigation -->
          <div class="flex items-center flex-1 min-w-0">
            <router-link to="/">
              <img class="h-12 lg:h-13 flex-shrink-0" src="../assets/logo.png" alt="红土证券" />
            </router-link>
            <!-- Desktop Navigation -->
            <nav class="hidden lg:flex items-center ml-6 space-x-6">
              <component
                v-for="item in navItems"
                :key="item.key"
                :is="getLinkComponent(item.key)"
                v-bind="getLinkProps(item.key)"
                class="text-base font-medium text-gray-700 hover:text-hongtu-primary transition-colors whitespace-nowrap"
              >
                {{ item.text }}
              </component>
            </nav>
          </div>

          <!-- Right side: Search and Auth -->
          <div class="flex items-center gap-3 lg:gap-4">
            <!-- Desktop Search -->

         
            <!-- Desktop Auth Button -->
            <component
              :is="getLinkComponent('nav_login')"
              v-bind="getLinkProps('nav_login')"
              class="hidden lg:block bg-transparent text-hongtu-primary   text-sm font-medium transition-colors"
            >
              {{ getNavText('nav_login', getLoginDefaultText()) }} 
            </component>
            <component
              :is="getLinkComponent('nav_register')"
              v-bind="getLinkProps('nav_register')"
              class="hidden lg:block bg-transparent text-hongtu-primary  text-sm font-medium transition-colors"
            >
              {{ getNavText('nav_register', getRegisterDefaultText()) }}
            </component>

            <!-- Mobile Menu Button -->
            <MobileMenu
              :nav-items="navItems"
              :top-nav-items="topNavItems"
              :content-data="contentData"
              :login-text="getNavText('nav_login', getLoginDefaultText())"
              :register-text="getNavText('nav_register', getRegisterDefaultText())"
            />
          </div>
        </div>
      </div>
    </header>
  </div>
</template>

<script>
import MobileMenu from './MobileMenu.vue'
import { ContentMixin, getContentsByCategory, normalizeLocale } from '../services/contentService'

// 主导航配置
const MAIN_NAV_CONFIG = [
  { key: 'nav_why_choose', i18nKey: 'nav.whyChoose' },
  { key: 'nav_stock_investment', i18nKey: 'nav.stockInvestment' },
  { key: 'nav_fund_investment', i18nKey: 'nav.fundInvestment' },
  { key: 'nav_account_delegate', i18nKey: 'nav.accountDelegate' },
  { key: 'nav_insurance_investment', i18nKey: 'nav.insuranceInvestment' },
  { key: 'nav_online_trading', i18nKey: 'nav.onlineTrading' }
]

// 顶部导航配置
const TOP_NAV_CONFIG = [
  { key: 'top_stock_customer', i18nKey: 'nav.stockCustomer' },
  { key: 'top_fund_customer', i18nKey: 'nav.fundCustomer' },
  { key: 'top_delegate_customer', i18nKey: 'nav.delegateCustomer' },
  { key: 'top_insurance_customer', i18nKey: 'nav.insuranceCustomer' },
  { key: 'nav_download', i18nKey: 'nav.download' },
  { key: 'nav_help', i18nKey: 'nav.help' },
  { key: 'nav_about', i18nKey: 'nav.about' }
]

// 默认路由配置（后端未配置时使用）
const DEFAULT_ROUTES = {
  // 主导航路由
  nav_why_choose: { route: '/why-choose', route_type: 'internal' },
  nav_stock_investment: { route: '/stock-investment', route_type: 'internal' },
  nav_fund_investment: { route: '/fund-investment', route_type: 'internal' },
  nav_account_delegate: { route: '/account-delegate', route_type: 'internal' },
  nav_insurance_investment: { route: '/insurance-investment', route_type: 'internal' },
  nav_online_trading: { route: '/online-trading', route_type: 'internal' },
  // 顶部导航路由
  top_stock_customer: { route: '/stock-customers', route_type: 'internal' },
  top_fund_customer: { route: '/fund-investment', route_type: 'internal' },
  top_delegate_customer: { route: '/account-delegate', route_type: 'internal' },
  top_insurance_customer: { route: '/insurance-investment', route_type: 'internal' },
  nav_download: { route: '/download', route_type: 'internal' },
  nav_help: { route: '/help', route_type: 'internal' },
  nav_about: { route: '/about', route_type: 'internal' },
  // 登录注册路由
  nav_login: { route: '/login', route_type: 'internal' },
  nav_register: { route: '/register', route_type: 'internal' }
}

export default {
  name: 'LayoutTopNav',
  components: {
    MobileMenu
  },
  mixins: [ContentMixin],
  data() {
    return {
      locale: 'zh',
      navItems: [],
      topNavItems: []
    }
  },
  computed: {
    currentLang() {
      return this.locale || 'zh'
    }
  },
  async created() {
    const initialLocale = normalizeLocale(localStorage.getItem('locale') || (this.$i18n ? this.$i18n.locale : 'zh'))
    this.locale = initialLocale
    localStorage.setItem('locale', initialLocale)
    document.documentElement.lang = initialLocale
    if (this.$i18n) {
      this.$i18n.locale = initialLocale
    }

    // 监听语言切换事件
    window.addEventListener('localeChange', this.handleLocaleChange)

    // 加载导航配置
    await this.loadNavigation()
  },
  beforeDestroy() {
    window.removeEventListener('localeChange', this.handleLocaleChange)
  },
  methods: {
    async loadNavigation() {
      try {
        // 从后端获取导航配置
        const navData = await getContentsByCategory('nav', this.currentLang)

        // 合并后端数据和默认路由配置（后端配置优先）
        this.contentData = {
          ...DEFAULT_ROUTES,
          ...navData
        }

        // 构建主导航项
        this.navItems = MAIN_NAV_CONFIG.map(item => ({
          key: item.key,
          text: this.getNavText(item.key, this.$t(item.i18nKey))
        }))

        // 构建顶部导航项
        this.topNavItems = TOP_NAV_CONFIG.map(item => ({
          key: item.key,
          text: this.getNavText(item.key, this.$t(item.i18nKey))
        }))

      } catch (error) {
        console.error('加载导航配置失败:', error)
        // 加载失败时使用默认配置
        this.setDefaultNavigation()
      }
    },

    setDefaultNavigation() {
      // 使用默认路由配置
      this.contentData = { ...DEFAULT_ROUTES }

      // 构建主导航项
      this.navItems = MAIN_NAV_CONFIG.map(item => ({
        key: item.key,
        text: this.$t(item.i18nKey)
      }))

      // 构建顶部导航项
      this.topNavItems = TOP_NAV_CONFIG.map(item => ({
        key: item.key,
        text: this.$t(item.i18nKey)
      }))
    },

    // 获取导航文本内容
    getNavText(key, defaultValue = '') {
      const item = this.contentData[key]
      if (item && typeof item === 'object' && item.text) {
        return item.text  // route类型的文本内容
      }
      if (typeof item === 'string') {
        return item  // text类型的直接内容
      }
      return defaultValue
    },

    // 获取链接组件类型
    getLinkComponent(key) {
      const item = this.contentData[key]
      const routeType = item && typeof item === 'object' && item.route_type ? item.route_type : 'external'
      return routeType === 'internal' ? 'router-link' : 'a'
    },

    // 获取链接属性
    getLinkProps(key) {
      const item = this.contentData[key]
      const route = item && typeof item === 'object' && item.route ? item.route : '#'
      const routeType = item && typeof item === 'object' && item.route_type ? item.route_type : 'external'

      if (routeType === 'internal') {
        return { to: route }
      } else {
        // 确保外部链接包含协议
        let href = route
        if (href && !href.startsWith('http://') && !href.startsWith('https://')) {
          // 如果是纯域名，添加https协议
          if (href.includes('.') && !href.includes('/')) {
            href = 'https://' + href
          }
        }
        return { href, target: '_blank', rel: 'noopener noreferrer' }
      }
    },

    getLoginDefaultText() {
      if (this.currentLang === 'en') return 'Login'
      if (this.currentLang === 'zh-Hant') return '登錄'
      return '登录'
    },

    getRegisterDefaultText() {
      if (this.currentLang === 'en') return 'Register'
      if (this.currentLang === 'zh-Hant') return '註冊'
      return '注册'
    },

    handleLocaleChange(event) {
      const nextLocale = normalizeLocale(event && event.detail ? event.detail.locale : this.locale)
      this.locale = nextLocale
      localStorage.setItem('locale', nextLocale)
      document.documentElement.lang = nextLocale
      if (this.$i18n) {
        this.$i18n.locale = nextLocale
      }
      this.loadNavigation()
    },

    onLocaleChange(e) {
      const val = normalizeLocale(e.target.value)
      this.locale = val
      localStorage.setItem('locale', val)
      document.documentElement.lang = val
      if (this.$i18n) {
        this.$i18n.locale = val
      }

      // 手动触发语言切换事件，通知其他组件更新
      window.dispatchEvent(new CustomEvent('localeChange', {
        detail: { locale: val }
      }))
    }
  }
}
</script>
